'use client';

import dynamic from 'next/dynamic';
import NextLink from 'next/link';

import { type CSSProperties, memo, useCallback, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { ListingTile } from '@nocowanie/noc-api';

import { IconsNocowanie } from '@nocowanie/icons/nocowanie';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile/accommodation-tile.props';
import { useAccommodationTileData } from '@app/components/molecules/accommodation-tile/use-accommodation-tile-data';
import { environment } from '@app/environments/environment';

import s from './accommodation-tile-lite.module.css';
import { TileAmenities } from './tile-amenities';
import { TileBadges } from './tile-badges';
import { TileDetails } from './tile-details';
import { TileIcon } from './tile-icon';
import { TileImage } from './tile-image';
import { TileLocation } from './tile-location';
import { TilePriceColumn } from './tile-price';
import { TileRating } from './tile-rating';

const AccommodationTileCarousel = dynamic(
    () =>
        import('@app/components/molecules/accommodation-tile/accommodation-tile-carousel').then(
            mod => mod.AccommodationTileCarousel,
        ),
    {
        ssr: false,
        loading: () => null,
    },
);

// Same props as `AccommodationTile`, minus its Chakra slot recipe `styles` (Chakra style props are ignored)
type AccommodationTileLiteProps = Omit<AccommodationTileProps, 'isESI' | 'styles'>;

/**
 * Listing tile rendered with plain elements and a static CSS module instead of Chakra
 * components. Same markup, test ids and styles as `AccommodationTile` (the CSS is generated
 * from its Chakra styles), but hydrating/rendering it doesn't run Chakra/Emotion for every
 * element, which made the 30 listing tiles the most expensive part of hydration.
 * `AccommodationTile` is still used where Emotion must inline the styles (ESI partials).
 */
const AccommodationTileLiteComponent = ({
    searchCriteria = {},
    searchFilters = {},
    position,
    amenitiesMaxVisible,
    isMobileBrowser,
    priorityCount = Math.max(
        environment.HIGH_PRIORITY_IMAGE_COUNT_MOBILE,
        environment.HIGH_PRIORITY_IMAGE_COUNT_DESKTOP,
    ),
    initialData = null,
    t,
    cityLocation,
    locationVarieties,
    style,
}: AccommodationTileLiteProps) => {
    const [isCarouselLoaded, setIsCarouselLoaded] = useState(false);
    const onCarouselLoaded = useCallback(() => setIsCarouselLoaded(true), []);
    const { withDates, urls, nightsCount } = useAccommodationTileData(
        initialData,
        searchCriteria,
        searchFilters,
    );

    if (!initialData) {
        return null;
    }

    const highPriorityCount = isMobileBrowser
        ? environment.HIGH_PRIORITY_IMAGE_COUNT_MOBILE
        : environment.HIGH_PRIORITY_IMAGE_COUNT_DESKTOP;
    const withHighPriority = isMobileBrowser
        ? position <= highPriorityCount
        : position <= priorityCount;

    const {
        id,
        name,
        images,
        features,
        path,
        source,
        listing: { placement = undefined } = {},
        location: { settlement = undefined, latitude, longitude } = {},
        rating,
    } = initialData;

    const isInquiry = source === ListingTile.source.NOC;
    const isOTA = source === ListingTile.source.NOP;
    const coordinates = latitude && longitude ? { lat: latitude, lon: longitude } : undefined;
    const image = (
        <TileImage
            accommodationName={name ?? ''}
            images={images ?? []}
            withHighPriority={withHighPriority}
            isOTA={isOTA}
        />
    );

    return (
        <article
            data-testid={`acc-tile-${source}`}
            data-id={id}
            className={s.tile}
            style={style as CSSProperties | undefined}
        >
            <div data-testid={'acc-tile-images'} className={s.images}>
                {isMobileBrowser ? (
                    image
                ) : (
                    <>
                        {/* Component needs to be rendered to actually trigger dynamic import */}
                        <AccommodationTileCarousel
                            accommodationName={name ?? ''}
                            images={images ?? []}
                            isOTA={isOTA}
                            onMounted={onCarouselLoaded}
                            isHidden={!isCarouselLoaded}
                        />
                        {isCarouselLoaded ? null : image}
                    </>
                )}
            </div>

            <div data-testid={'acc-tile-info'} className={s.info}>
                <TileBadges
                    source={source}
                    reservationType={features?.internalLabel?.reservationType}
                    isBestseller={features?.bestseller}
                    listingPlacement={placement}
                    t={t}
                />
                <div className={s.nameRow}>
                    {features?.flagged ? (
                        <div className={s.overlayTooltipRoot}>
                            <TileIcon icon={IconsNocowanie.Sponsored} className={s.flaggedIcon} />
                            <div
                                className={`${s.tooltipContent} ${s.tooltipFlagged}`}
                                data-testid={'tooltip-content'}
                            >
                                {t('badges.tooltips.flagged')}
                            </div>
                        </div>
                    ) : null}
                    <h3 data-testid={'acc-tile-name'} className={s.name}>
                        {isInquiry ? (
                            <a
                                className={s.nameLink}
                                href={path}
                                dangerouslySetInnerHTML={{ __html: name || '' }}
                            />
                        ) : (
                            <NextLink
                                className={s.nameLink}
                                href={urls.accommodation}
                                prefetch={false}
                                dangerouslySetInnerHTML={{ __html: name || '' }}
                            />
                        )}
                    </h3>
                </div>
                {id && placement && name && settlement?.name ? (
                    <TileLocation
                        accommodation={{
                            id,
                            listingPlacement: placement,
                            name,
                            settlementName: settlement.name,
                            isOTA,
                            ...(coordinates && { coordinates }),
                        }}
                        searchCriteria={searchCriteria}
                        mapUrl={urls.map}
                        beachDistanceMeters={initialData.distances?.beach?.meters ?? undefined}
                        cityLocation={cityLocation}
                        varietyGenitive={
                            locationVarieties?.genitive?.length
                                ? locationVarieties?.genitive
                                : locationVarieties?.nominative
                        }
                        t={t}
                    />
                ) : null}
            </div>

            <TileRating
                ratingAverage={rating?.ratingsAverage ?? undefined}
                ratingCount={rating?.ratingCount ?? undefined}
                ratingDescription={rating?.ratingDescription}
                t={t}
            />

            <div data-testid={'acc-tile-amenities'} className={s.amenities}>
                <TileAmenities
                    amenities={features?.internalAmenityGroups}
                    maxVisible={amenitiesMaxVisible}
                    isInquiry={isInquiry}
                    t={t}
                />
            </div>

            <TileDetails tile={initialData} t={t} />

            <TilePriceColumn
                tile={initialData}
                searchCriteria={searchCriteria}
                withDates={withDates}
                nightsCount={nightsCount}
                t={t}
            />
        </article>
    );
};

const areTilePropsEqual = (prev: AccommodationTileLiteProps, next: AccommodationTileLiteProps) =>
    prev.initialData === next.initialData &&
    prev.position === next.position &&
    prev.priorityCount === next.priorityCount &&
    prev.amenitiesMaxVisible === next.amenitiesMaxVisible &&
    prev.isMobileBrowser === next.isMobileBrowser &&
    isEqual(prev.cityLocation, next.cityLocation) &&
    isEqual(prev.locationVarieties, next.locationVarieties) &&
    isEqual(prev.searchCriteria, next.searchCriteria) &&
    isEqual(prev.searchFilters, next.searchFilters) &&
    prev.t === next.t;

export const AccommodationTileLite = memo(AccommodationTileLiteComponent, areTilePropsEqual);
